import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type {
  ChecklistOfferGroup,
  EstimateLine,
  FinishStandard,
  RegionId,
  RenovationType,
  Room,
  WizardInput,
} from "@/types/mvpEstimate";
import { roundMoney } from "@/utils/mvpEstimateMath";

type GeneratorDatabase = Omit<Database, "public"> & {
  public: Omit<Database["public"], "Tables"> & {
    Tables: Database["public"]["Tables"] & {
      work_templates: {
        Row: {
          id: string;
          generator_key: string | null;
        };
        Insert: { id?: string; generator_key?: string | null };
        Update: { id?: string; generator_key?: string | null };
        Relationships: [];
      };
    };
  };
};

type WorkTemplateRow = GeneratorDatabase["public"]["Tables"]["work_templates"]["Row"];
type EstimateLineWithTemplateMeta = EstimateLine & {
  work_template_id?: string | null;
  generator_key?: string | null;
};
const supabaseGenerator = supabase as unknown as SupabaseClient<GeneratorDatabase>;

function assignWorkTemplateIdsByGeneratorKey(
  lines: EstimateLineWithTemplateMeta[],
  templates: WorkTemplateRow[],
): EstimateLine[] {
  const idByGeneratorKey = new Map<string, string>();
  for (const template of templates) {
    if (!template.generator_key) continue;
    idByGeneratorKey.set(template.generator_key, template.id);
  }

  return lines.map((line) => {
    const key = line.generator_key?.trim();
    if (!key) return line;
    const workTemplateId = idByGeneratorKey.get(key);
    if (!workTemplateId) return line;
    return { ...line, work_template_id: workTemplateId };
  });
}

const STANDARD_MULT: Record<FinishStandard, number> = {
  ekonomiczny: 0.88,
  standard: 1,
  premium: 1.18,
};

const REGION_MULT: Record<RegionId, number> = {
  mazowieckie: 1.12,
  aglomeracja: 1.06,
  regionalne: 1,
  inne: 0.95,
};

/** Wysokość pomieszczenia — do obliczenia pow. ścian z metrażu. */
const WALL_HEIGHT_M = 2.3;

/** Wartości używane w UI („Założenia”) — identyczne z `heuristicAreas` (bez zmiany logiki). */
export const MVP_GENERATOR_WALL_HEIGHT_M = WALL_HEIGHT_M;
/** m² ścian = metraż × ten przelicznik (w heurystyce równy wysokości w m). */
export const MVP_GENERATOR_WALL_AREA_MULTIPLIER = WALL_HEIGHT_M;

function newId(): string {
  return crypto.randomUUID();
}

/**
 * Łączy linie tego samego typu (ta sama nazwa, jednostka i stawki) — sumuje ilości.
 * Klucz: `name` + `unit` + stawki + marża — nie `id` (każda linia ma losowe UUID z generatora).
 */
function mergeLineKey(line: EstimateLine): string {
  return [
    line.name,
    line.unit,
    line.laborUnitPrice,
    line.materialUnitPrice,
    line.marginPercent,
  ].join("\0");
}

export function mergeEstimateLines(lines: EstimateLine[]): EstimateLine[] {
  const map = new Map<string, EstimateLine>();
  for (const line of lines) {
    const key = mergeLineKey(line);
    const existing = map.get(key);
    if (existing) {
      map.set(key, {
        ...existing,
        quantity: roundMoney(existing.quantity + line.quantity),
      });
    } else {
      map.set(key, { ...line });
    }
  }
  return Array.from(map.values());
}

/** Eksportowane pod unit testy (Pass 3). */
export function computeRoomTotals(rooms: Room[]): { totalArea: number; avgWallHeight: number } {
  const totalArea = rooms.reduce((s, r) => s + r.areaM2, 0);
  if (totalArea <= 0) return { totalArea: 0, avgWallHeight: 2.6 };
  const avgWallHeight = rooms.reduce((s, r) => s + r.areaM2 * r.wallHeight, 0) / totalArea;
  return { totalArea, avgWallHeight };
}

/**
 * Powierzchnie heurystyczne:
 * - ściany ≈ area * 2.3
 * - sufit = area
 * - podłoga = area
 */
export function heuristicAreas(totalArea: number): { walls: number; ceiling: number; floor: number } {
  const a = Math.max(0, totalArea);
  return {
    walls: roundMoney(a * WALL_HEIGHT_M),
    ceiling: roundMoney(a),
    floor: roundMoney(a),
  };
}

/** Szacunek długości listew: obwód „średniego” pomieszczenia ~4×√powierzchnia (mb). */
function listwyLinearM(totalArea: number): number {
  const a = Math.max(totalArea, 1);
  return roundMoney(Math.max(4, 4 * Math.sqrt(a)));
}

function priceMultiplier(standard: FinishStandard, region: RegionId): number {
  return STANDARD_MULT[standard] * REGION_MULT[region];
}

export type GenerateEstimateOptions = {
  /** Domyślna marża % dla pozycji głównych; pozycje instalacyjne ~80% tej wartości (min. 8%). */
  defaultLineMarginPercent?: number;
};

/**
 * Jedno pomieszczenie — linie z `room.scopeIds` i heurystyką z `room.areaM2` (bez korekty typu budynku).
 * Eksport pod testy (Pass 4).
 */
export function generateLinesForRoom(
  room: Room,
  standard: FinishStandard,
  region: RegionId,
  lineVatRate: number,
  options?: GenerateEstimateOptions
): EstimateLine[] {
  const primary = options?.defaultLineMarginPercent ?? 15;
  const secondary = Math.max(8, Math.round(primary * 0.8));
  const scopeIds = room.scopeIds;
  const roomArea = Math.max(0, room.areaM2);
  const m = priceMultiplier(standard, region);
  const { walls, ceiling, floor } = heuristicAreas(roomArea);

  const lines: EstimateLineWithTemplateMeta[] = [];
  const push = (
    partial: Omit<
      EstimateLineWithTemplateMeta,
      | "id"
      | "source"
      | "vatRate"
      | "sourceType"
      | "confidence"
      | "reviewStatus"
      | "assumptionText"
      | "notes"
      | "quantityBasis"
    >
  ) => {
    lines.push({
      ...partial,
      id: newId(),
      source: "generator",
      vatRate: lineVatRate,
      sourceType: "heuristic",
      confidence: "medium",
      reviewStatus: "draft",
      assumptionText: null,
      notes: null,
      quantityBasis: null,
    });
  };

  const hasMalowaniePelne = scopeIds.includes("sciany");
  const hasMalowanieScian = scopeIds.includes("sciany_tylko");
  const hasSufitOsobno = scopeIds.includes("sufit") && !hasMalowaniePelne;
  const hasPodloga = scopeIds.includes("podloga");
  const hasPlytkiCeramiczne = scopeIds.includes("plytki_ceramiczne");

  // ── Malowanie pełne: ściany + sufit + sprzątanie ─────────────────────────────
  if (hasMalowaniePelne) {
    push({
      generator_key: "room_protection",
      name: "Zabezpieczenie pomieszczenia (folie, osłony, ochrona podłóg i mebli)",
      quantity: 1,
      unit: "kpl.",
      laborUnitPrice: roundMoney(320 * m),
      materialUnitPrice: roundMoney(140 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "paint_walls_full",
      name: "Przygotowanie i malowanie ścian (grunt, 2 warstwy farby)",
      quantity: walls,
      unit: "m²",
      laborUnitPrice: roundMoney(34 * m),
      materialUnitPrice: roundMoney(22 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "paint_ceiling_full",
      name: "Malowanie sufitu (2 warstwy farby, przygotowanie podłoża w cenie pozycji)",
      quantity: ceiling,
      unit: "m²",
      laborUnitPrice: roundMoney(18 * m),
      materialUnitPrice: roundMoney(10 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "cleanup_after_paint",
      name: "Sprzątanie po pracach malarskich (odnoszenie folii, odkurzanie)",
      quantity: 1,
      unit: "szt.",
      laborUnitPrice: roundMoney(260 * m),
      materialUnitPrice: roundMoney(45 * m),
      marginPercent: primary,
    });
  }

  // ── Malowanie tylko ścian (bez sufitu w tej paczce) ───────────────────────────
  if (hasMalowanieScian && !hasMalowaniePelne) {
    push({
      generator_key: "room_protection",
      name: "Zabezpieczenie pomieszczenia (folie, osłony, ochrona podłóg i mebli)",
      quantity: 1,
      unit: "kpl.",
      laborUnitPrice: roundMoney(280 * m),
      materialUnitPrice: roundMoney(120 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "paint_walls_no_ceiling",
      name: "Przygotowanie i malowanie ścian (grunt, 2 warstwy farby) — bez sufitu w zakresie",
      quantity: walls,
      unit: "m²",
      laborUnitPrice: roundMoney(34 * m),
      materialUnitPrice: roundMoney(22 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "cleanup_after_paint",
      name: "Sprzątanie po pracach malarskich (odnoszenie folii, odkurzanie)",
      quantity: 1,
      unit: "szt.",
      laborUnitPrice: roundMoney(220 * m),
      materialUnitPrice: roundMoney(40 * m),
      marginPercent: primary,
    });
  }

  if (hasSufitOsobno) {
    push({
      generator_key: "paint_ceiling_only",
      name: "Przygotowanie i malowanie sufitu (grunt i 2 warstwy farby) — zakres tylko sufit",
      quantity: ceiling,
      unit: "m²",
      laborUnitPrice: roundMoney(28 * m),
      materialUnitPrice: roundMoney(17 * m),
      marginPercent: primary,
    });
  }

  // ── Podłogi: 3 pozycje (przygotowanie + panele w jednej linii) ─────────────────
  if (hasPodloga) {
    const lm = listwyLinearM(roomArea);
    push({
      generator_key: "prep_lay_panels",
      name: "Przygotowanie podłoża pod panele i układanie paneli (docinanie, dylatacje)",
      quantity: floor,
      unit: "m²",
      laborUnitPrice: roundMoney(46 * m),
      materialUnitPrice: roundMoney(35 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "baseboard_install",
      name: "Listwy przypodłogowe (montaż, docięcie pod kątem)",
      quantity: lm,
      unit: "mb",
      laborUnitPrice: roundMoney(9 * m),
      materialUnitPrice: roundMoney(6 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "floor_finishing_details",
      name: "Wykończenia podłogowe (progi przejściowe, detale)",
      quantity: 1,
      unit: "szt.",
      laborUnitPrice: roundMoney(220 * m),
      materialUnitPrice: roundMoney(95 * m),
      marginPercent: primary,
    });
  }

  // ── Płytki ceramiczne / gres (posadzka) — osobno od paneli (`podloga`) ────────
  if (hasPlytkiCeramiczne) {
    push({
      generator_key: "prep_subfloor_tiles",
      name: "Przygotowanie podłoża pod płytki (wyrównanie, grunt, mechaniczne czyszczenie)",
      quantity: floor,
      unit: "m²",
      laborUnitPrice: roundMoney(42 * m),
      materialUnitPrice: roundMoney(28 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "lay_tiles_gres",
      name: "Układanie płytek ceramicznych / gresu na klej (dylatacje, docinki)",
      quantity: floor,
      unit: "m²",
      laborUnitPrice: roundMoney(62 * m),
      materialUnitPrice: roundMoney(48 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "grout_joints",
      name: "Fugowanie spoin (dopasowanie koloru fugi — szacunek zbiorczy)",
      quantity: floor,
      unit: "m²",
      laborUnitPrice: roundMoney(22 * m),
      materialUnitPrice: roundMoney(14 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "tile_hydroinsulation",
      name: "Hydroizolacja pod płytki (mata / masa — strefa mokra, szacunek z m²)",
      quantity: floor,
      unit: "m²",
      laborUnitPrice: roundMoney(28 * m),
      materialUnitPrice: roundMoney(38 * m),
      marginPercent: primary,
    });
  }

  // ── Hydraulika: kilka pozycji ─────────────────────────────────────────────
  if (scopeIds.includes("hydraulika")) {
    const points = Math.max(1, Math.ceil(roomArea / 25));
    const armatur = Math.max(1, Math.ceil(points / 2));
    push({
      generator_key: "plumbing_distribution",
      name: "Prace hydrauliczne — rozprowadzenie (rury, punkty zgodnie z układem)",
      quantity: points,
      unit: "szt.",
      laborUnitPrice: roundMoney(175 * m),
      materialUnitPrice: roundMoney(85 * m),
      marginPercent: secondary,
    });
    push({
      generator_key: "install_faucets_connections",
      name: "Montaż baterii i przyłączy (montaż, podłączenie pod zasilanie)",
      quantity: armatur,
      unit: "szt.",
      laborUnitPrice: roundMoney(140 * m),
      materialUnitPrice: roundMoney(70 * m),
      marginPercent: secondary,
    });
    push({
      generator_key: "plumbing_pressure_test",
      name: "Próby szczelności i odbiór instalacji (ciśnienie, wizualnie)",
      quantity: 1,
      unit: "szt.",
      laborUnitPrice: roundMoney(160 * m),
      materialUnitPrice: roundMoney(55 * m),
      marginPercent: secondary,
    });
  }

  // ── Elektryka: kilka pozycji ─────────────────────────────────────────────
  if (scopeIds.includes("elektryka")) {
    const points = Math.max(1, Math.ceil(roomArea / 20));
    const osprzet = Math.max(1, Math.ceil(points / 2));
    push({
      generator_key: "electrical_points_dist",
      name: "Punkty elektryczne — rozprowadzenie (kable, puszki, zabezpieczenia)",
      quantity: points,
      unit: "szt.",
      laborUnitPrice: roundMoney(115 * m),
      materialUnitPrice: roundMoney(42 * m),
      marginPercent: secondary,
    });
    push({
      generator_key: "connect_electrical_fit",
      name: "Podłączenia osprzętu (gniazda, wyłączniki, osłony)",
      quantity: osprzet,
      unit: "szt.",
      laborUnitPrice: roundMoney(85 * m),
      materialUnitPrice: roundMoney(35 * m),
      marginPercent: secondary,
    });
    push({
      generator_key: "electrical_measure_accept",
      name: "Pomiary i odbiór obwodów (bezpieczeństwo, zgodność z normą)",
      quantity: 1,
      unit: "szt.",
      laborUnitPrice: roundMoney(130 * m),
      materialUnitPrice: roundMoney(40 * m),
      marginPercent: secondary,
    });
  }

  // ── Drobne naprawy / przygotowanie (szacunek zgrubny) ───────────────────────
  if (scopeIds.includes("naprawy")) {
    const patchM2 = roundMoney(Math.max(3, walls * 0.08));
    push({
      generator_key: "minor_repairs_prep",
      name: "Drobne naprawy podłoża i przygotowanie (łaty, ubytki, punktowe szlifowanie)",
      quantity: 1,
      unit: "kpl.",
      laborUnitPrice: roundMoney(420 * m),
      materialUnitPrice: roundMoney(180 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "patching_spot_prime",
      name: "Uzupełnienia po kołkach, drobne szpachlowanie, gruntowanie punktowe (orientacyjnie)",
      quantity: patchM2,
      unit: "m²",
      laborUnitPrice: roundMoney(28 * m),
      materialUnitPrice: roundMoney(14 * m),
      marginPercent: primary,
    });
  }

  // ── Drobne montaże / wykończenia (szacunek zgrubny) ─────────────────────────
  if (scopeIds.includes("montaze")) {
    push({
      generator_key: "minor_finish_install",
      name: "Drobne montaże wykończeniowe (listwy, ościeżnice, progi — orientacyjnie kpl.)",
      quantity: 1,
      unit: "kpl.",
      laborUnitPrice: roundMoney(380 * m),
      materialUnitPrice: roundMoney(220 * m),
      marginPercent: primary,
    });
    push({
      generator_key: "tightening_acrylic_det",
      name: "Dokręczenia, akryle, detale montażowe (punktowo)",
      quantity: 1,
      unit: "szt.",
      laborUnitPrice: roundMoney(260 * m),
      materialUnitPrice: roundMoney(90 * m),
      marginPercent: primary,
    });
  }

  return lines;
}

/**
 * Generuje rozbudowany kosztorys — dla każdego pokoju z metrażem i zakresem, na końcu scala te same pozycje.
 * `lineVatRate` — ułamek VAT (np. z globalnego wyboru w ofercie).
 */
export function generateEstimateLines(
  input: WizardInput,
  lineVatRate = 0,
  options?: GenerateEstimateOptions
): EstimateLine[] {
  const allLines: EstimateLine[] = [];
  const roomList = input.rooms ?? [];

  for (const room of roomList) {
    if (room.areaM2 <= 0) continue;
    const scopeIds = room.scopeIds;
    if (!Array.isArray(scopeIds) || scopeIds.length === 0) continue;
    allLines.push(
      ...generateLinesForRoom(room, input.standard, input.region, lineVatRate, options),
    );
  }
  const lines = mergeEstimateLines(allLines);

  const legacyReno =
    (input as WizardInput & { renovationType?: RenovationType }).renovationType ?? "mieszkanie";
  const typeAdj =
    legacyReno === "dom" ? 1.05 : legacyReno === "biuro" ? 1.03 : 1;
  if (typeAdj !== 1 && lines.length > 0) {
    return lines.map((l) => ({
      ...l,
      laborUnitPrice: roundMoney(l.laborUnitPrice * typeAdj),
      materialUnitPrice: roundMoney(l.materialUnitPrice * typeAdj),
    }));
  }

  return lines;
}

/**
 * Wariant asynchroniczny:
 * 1) pobiera work_templates z Supabase
 * 2) generuje linie heurystycznie
 * 3) przypina work_template_id po generator_key
 */
export async function generateEstimateLinesWithTemplates(
  input: WizardInput,
  lineVatRate = 0,
  options?: GenerateEstimateOptions
): Promise<EstimateLine[]> {
  const generated = generateEstimateLines(input, lineVatRate, options);
  if (generated.length === 0) return generated;

  const { data, error } = await supabaseGenerator
    .from("work_templates")
    .select("id, generator_key");

  if (error || !data || data.length === 0) return generated;
  return assignWorkTemplateIdsByGeneratorKey(
    generated as EstimateLineWithTemplateMeta[],
    data,
  );
}

export function emptyEstimateLine(vatRate = 0, marginPercent = 15): EstimateLine {
  const line: EstimateLineWithTemplateMeta = {
    id: newId(),
    generator_key: "manual_new_line",
    name: "Nowa pozycja",
    quantity: 1,
    unit: "szt.",
    laborUnitPrice: 0,
    materialUnitPrice: 0,
    marginPercent,
    source: "manual",
    vatRate,
    sourceType: "manual_edit",
    confidence: "medium",
    reviewStatus: "draft",
    assumptionText: null,
    notes: null,
    quantityBasis: null,
  };
  return line;
}

/** Szkic pozycji z checklisty — zawsze 0 zł, bez auto-wyceny; sekcja oferty w `checklistOfferGroup`. */
export function estimateLineFromChecklistDraft(
  name: string,
  vatRate = 0,
  offerGroup?: ChecklistOfferGroup
): EstimateLine {
  return {
    ...emptyEstimateLine(vatRate, 0),
    name,
    marginPercent: 0,
    laborUnitPrice: 0,
    materialUnitPrice: 0,
    reviewStatus: "needs_review",
    confidence: "low",
    sourceType: "template",
    notes: "Dodane z checklisty „Sprawdź kosztorys”.",
    assumptionText: "Do uzupełnienia — brak automatycznej wyceny.",
    checklistOfferGroup: offerGroup,
  };
}
